import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import deFlag from '../assets/icons/flags/DE.svg';
import atFlag from '../assets/icons/flags/AT.svg';
import chFlag from '../assets/icons/flags/CH.svg';
import frFlag from '../assets/icons/flags/FR.svg';
import itFlag from '../assets/icons/flags/IT.svg';
import nlFlag from '../assets/icons/flags/NL.svg';
import beFlag from '../assets/icons/flags/BE.svg';
import luFlag from '../assets/icons/flags/LU.svg';
import gbFlag from '../assets/icons/flags/GB.svg';
import ieFlag from '../assets/icons/flags/IE.svg';
import dkFlag from '../assets/icons/flags/DK.svg';
import seFlag from '../assets/icons/flags/SE.svg';
import noFlag from '../assets/icons/flags/NO.svg';
import fiFlag from '../assets/icons/flags/FI.svg';
import esFlag from '../assets/icons/flags/ES.svg';
import ptFlag from '../assets/icons/flags/PT.svg';
import plFlag from '../assets/icons/flags/PL.svg';
import czFlag from '../assets/icons/flags/CZ.svg';
import skFlag from '../assets/icons/flags/SK.svg';
import huFlag from '../assets/icons/flags/HU.svg';


interface Country {
  code: string;
  name: string;
  flag: string;
  icon: string;
}

interface CountryInputProps {
  id: string;
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const CountryInput: React.FC<CountryInputProps> = ({
  id,
  label,
  value,
  error,
  required = false,
  onChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const countries: Country[] = [
    { code: 'DE', name: t('country_germany'), flag: 'DE', icon: deFlag },
    { code: 'AT', name: t('country_austria'), flag: 'AT', icon: atFlag },
    { code: 'CH', name: t('country_switzerland'), flag: 'CH', icon: chFlag },
    { code: 'FR', name: t('country_france'), flag: 'FR', icon: frFlag },
    { code: 'IT', name: t('country_italy'), flag: 'IT', icon: itFlag },
    { code: 'NL', name: t('country_netherlands'), flag: 'NL', icon: nlFlag },
    { code: 'BE', name: t('country_belgium'), flag: 'BE', icon: beFlag },
    { code: 'LU', name: t('country_luxembourg'), flag: 'LU', icon: luFlag },
    { code: 'GB', name: t('country_united_kingdom'), flag: 'GB', icon: gbFlag },
    { code: 'IE', name: t('country_ireland'), flag: 'IE', icon: ieFlag },
    { code: 'DK', name: t('country_denmark'), flag: 'DK', icon: dkFlag },
    { code: 'SE', name: t('country_sweden'), flag: 'SE', icon: seFlag },
    { code: 'NO', name: t('country_norway'), flag: 'NO', icon: noFlag },
    { code: 'FI', name: t('country_finland'), flag: 'FI', icon: fiFlag },
    { code: 'ES', name: t('country_spain'), flag: 'ES', icon: esFlag },
    { code: 'PT', name: t('country_portugal'), flag: 'PT', icon: ptFlag },
    { code: 'PL', name: t('country_poland'), flag: 'PL', icon: plFlag },
    { code: 'CZ', name: t('country_czech_republic'), flag: 'CZ', icon: czFlag },
    { code: 'SK', name: t('country_slovakia'), flag: 'SK', icon: skFlag },
    { code: 'HU', name: t('country_hungary'), flag: 'HU', icon: huFlag },
  ];

  const selectedCountry = countries.find(country => country.code === value);

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (onBlur) {
          onBlur();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  const handleCountrySelect = (country: Country) => {
    onChange(country.code);
    setIsOpen(false);
    setSearchTerm('');
  };

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="form-field" ref={dropdownRef}>
      <label htmlFor={id}>{label}</label>
      <div className="country-input">
        <div
          className={`country-input__trigger ${error ? 'country-input__trigger--error' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {selectedCountry ? (
            <span className="country-input__selected">
              <span className="country-input__flag">
                <img src={selectedCountry.icon} alt={t('country_flag_alt', { country: selectedCountry.name })} />
              </span>
              <span className="country-input__name">{selectedCountry.name}</span>
            </span>
          ) : (
            <span className="country-input__placeholder">{t('country_select_placeholder')}</span>
          )}
          <span className={`country-input__arrow ${isOpen ? 'country-input__arrow--up' : 'country-input__arrow--down'}`} />
        </div>

        {isOpen && (
          <div className="country-input__dropdown">
            <input
              ref={inputRef}
              type="text"
              className="country-input__search"
              placeholder={t('country_search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="country-input__list">
              {filteredCountries.map(country => (
                <div
                  key={country.code}
                  className={`country-input__option ${value === country.code ? 'country-input__option--selected' : ''}`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="country-input__flag">
                    <img src={country.icon} alt={t('country_flag_alt', { country: country.name })} />
                  </span>
                  <span className="country-input__name">{country.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default CountryInput; 